import { cloneDeep, get, set } from 'lodash';
import type { ApiRequestOptions } from 'framework/api';
import assignmentValidation from './validation/assignment';
import { useKernelApi, usePeopleManagementApi } from 'infrastructure/api';
import { ResourceData, ResourceId, useCrudService } from 'framework/crud';
import { useDateService } from 'framework/utils';
import { FilterCondition } from 'framework/types';
import { AssignmentFormData, AssignmentType } from './types';
import assignmentsValidation, { createTeamSlotValidation } from './validation/assignmentsValidation';
import getConfirmAssignmentValidation from './validation/confirmAssignmentValidation';
import useAssignmentTargetService from './target/shared/assignmentTargetService';

export const ASSIGNMENT_PATH = '/assignments';
export const ASSIGNMENT_DELETE_MESSAGE = 'Do you really want to delete this assignment?';

export const VIEW_ASSIGNMENTS_TAB = 'RESOURCE_ASSIGNMENT_FE_READ_LIST';
export const READ_ASSIGNMENTS_LIST = 'RESOURCE_ASSIGNMENT_READ_LIST';
export const READ_EMPLOYEE_ASSIGNMENTS = 'READ_EMPLOYEE_ASSIGNMENTS';
export const READ_ASSIGNMENT = 'READ';
export const CREATE_ASSIGNMENT = 'CREATE_RESOURCE_ASSIGNMENT';
export const UPDATE_ASSIGNMENT = 'UPDATE';
export const DELETE_ASSIGNMENT = 'DELETE';
export const FE_DELETE_ASSIGNMENTS = 'RESOURCE_ASSIGNMENT_FE_DELETE';

export const ACTIVE_STATUS = 'ACTIVE';
export const FUTURE_STATUS = 'FUTURE';
export const PENDING_STATUS = 'PENDING';

export const AssignmentTypeLabels: Dictionary<string> = {
  [AssignmentType.INDIVIDUAL_ASSIGNMENT]: 'Individual assignment',
  [AssignmentType.GROUP_ASSIGNMENT]: 'Service assignment',
};

const STATUS_FILTER_KEY = 'filter.status.in';
const IS_VIRTUAL_FILTER_KEY = 'filter.isVirtual.eq';
export const END_DATE_FILTER_KEY = 'filter.to';
export const START_DATE_FILTER_KEY = 'filter.from';

const PROJECT_TARGET_TYPE = 'PROJECT';
const TARGET_SEARCH_PAGE_SIZE = 30;
const findBudgetById = (budgetInfo: any, budgetId: unknown) =>
  [get(budgetInfo, 'defaultBudget'), ...(get(budgetInfo, 'availableBudgets') ?? [])]
    .filter(Boolean)
    .find((budget: any) => String(budget.id) === String(budgetId)) ?? null;

const useAssignmentService = () => {
  const singleResourceUrl = '/resourceAssignments/:id';
  const {
    getById,
    search: baseSearch,
    searchAll: baseSearchAll,
    update: baseUpdate,
    create: baseCreate,
    deleteById: baseDeleteById,
    invalidateCache,
    ...baseCrudRequests
  } = useCrudService({
    singleResourceUrl,
    listResourceUrl: '/resourceAssignments',
    useService: useKernelApi,
  });
  const { getMaxDate, getMinDate, getToday, formatDateForAPI, getMomentDateObj } = useDateService();
  const today = getToday();
  const tomorrow = getToday().add(1, 'days');

  const {
    sendPostRequest: sendPostPeopleRequest,
    sendPutRequest: sendPutPeopleRequest,
    sendDeleteRequest: sendDeletePeopleRequest,
  } = usePeopleManagementApi();

  const { search: searchTargets } = useAssignmentTargetService();

  const getValidationSchema = () => Promise.resolve(assignmentValidation);
  const getConfirmAssignmentValidationSchema = (projectStartDate?: string) =>
    Promise.resolve(getConfirmAssignmentValidation(projectStartDate));

  const mapAssignmentWithSpof = <T extends { isSpof?: boolean }>(assignment: T): T => assignment;

  const createAssignment = async (assignment: AssignmentFormData) => {
    const mappedAssignment = mapForPeopleManagement(assignment);
    const response = await sendPostPeopleRequest('/resourceAssignments', mappedAssignment);
    const result = await response.json();

    invalidateCache?.();
    return result;
  };

  const create = async (data: ResourceData) => {
    const mappedAssignment = mapForPeopleManagement(data);
    const response = await sendPostPeopleRequest('/resourceAssignments', mappedAssignment);
    const result = await response.json();

    invalidateCache?.();
    return result;
  };

  const confirmVirtual = async (
    id: ResourceId,
    payload: {
      id: number;
      fromDate: string;
      toDate: string;
      minPercent: number;
      maxPercent: number;
    },
  ) => {
    const response = await sendPutPeopleRequest(`/resourceAssignments/${id}/confirm-virtual`, payload);
    return response.json();
  };

  const update = async (id: ResourceId, data: ResourceData) => {
    const mappedData = mapForPeopleManagement(data);
    const response = await sendPutPeopleRequest(`/resourceAssignments/${id}`, mappedData);
    const result = await response.json();

    invalidateCache?.();
    return result;
  };

  const deleteById = async (id: ResourceId) => {
    await sendDeletePeopleRequest(`/resourceAssignments/${id}`);
    invalidateCache?.();
  };

  const mapForPeopleManagement = (data: AssignmentFormData | ResourceData) => {
    let projectId: Nullable<StringOrNumber> = null;
    let serviceId: Nullable<StringOrNumber> = null;

    const targetType = get(data, 'target.type');
    const targetId = get(data, 'target.id');

    if (targetType === PROJECT_TARGET_TYPE) {
      projectId = targetId;
    } else {
      serviceId = targetId;
    }

    return {
      ownerAlias: get(data, 'ownerId'),
      employeeAlias: get(data, 'employeeId'),
      positionId: get(data, 'positionId'),
      technologyId: get(data, 'technologyId', null),
      roleOnProject: get(data, 'roleOnProject'),
      isBackup: get(data, 'backup', false),
      isSpof: get(data, 'isSpof'),
      isSecondaryLead: get(data, 'isSecondaryLead', false),
      backUpForAlias: get(data, 'backupForEmployeeId', null),
      isBench: get(data, 'bench', false),
      projectId,
      serviceId,
      fromDate: get(data, 'from'),
      toDate: get(data, 'to'),
      minPercent: get(data, 'minPercent'),
      maxPercent: get(data, 'maxPercent'),
      membershipRole: get(data, 'membershipRole', null),
      competencyLevel: get(data, 'competencyLevel', null),
      noteText: get(data, 'noteText', null),
      slotId: get(data, 'slotId', null),
    };
  };

  const fetchTargetBudgetInfo = async (targetId: ResourceId, displayName?: string) => {
    const response = await searchTargets({
      filter: { searchString: { ct: displayName ?? '' } },
      pageSize: TARGET_SEARCH_PAGE_SIZE,
    });
    const items = response?.result ?? [];
    const match = items.find((target: any) => String(target.id) === String(targetId));
    return match?.budgetInfo ?? null;
  };

  const getAssignmentById = async (id: string, ignoreCache?: boolean) => {
    const data = await getById(id, ignoreCache);
    const minPercentValue = get(data, 'minPercent');
    const maxPercentValue = get(data, 'maxPercent');

    const target = get(data, 'target');
    const existingBudgetInfo = get(target, 'budgetInfo');
    const budgetInfo =
      target?.id && target?.type === PROJECT_TARGET_TYPE && !existingBudgetInfo
        ? await fetchTargetBudgetInfo(target.id, target.displayName)
        : existingBudgetInfo;

    const budgetId = get(data, 'budgetId') ?? get(data, 'budget.id') ?? get(budgetInfo, 'defaultBudget.id') ?? null;
    const budget = get(data, 'budget') ?? findBudgetById(budgetInfo, budgetId);

    return {
      ...mapAssignmentWithSpof(data),
      ...(target ? { target: { ...target, budgetInfo } } : {}),
      budgetId,
      budget,
      projectId: get(data, 'project.id'),
      projectName: get(data, 'project.name'),
      positionId: get(data, 'position.id'),
      technologyId: get(data, 'technology.id'),
      roleOnProject: get(data, 'roleOnProject'),
      isSecondaryLead: get(data, 'isSecondaryLead', false),
      minPercent: minPercentValue || minPercentValue === 0 ? String(minPercentValue) : minPercentValue,
      maxPercent: maxPercentValue || maxPercentValue === 0 ? String(maxPercentValue) : maxPercentValue,
    };
  };

  const normalizeSearchParams = (request: ResourceData) => {
    const searchParams = cloneDeep(request);
    const hasFilterByActiveStatus = get(request, STATUS_FILTER_KEY)?.includes(ACTIVE_STATUS);
    const hasFilterByFutureStatus = get(request, STATUS_FILTER_KEY)?.includes(FUTURE_STATUS);
    const hasExplicitIsVirtualFilter = !!get(request, IS_VIRTUAL_FILTER_KEY);

    ['bench', 'isVirtual'].forEach(source => {
      const filterValue = searchParams.filter?.[source]?.eq;

      if (filterValue == null || filterValue === '' || !searchParams.filter) {
        return;
      }

      searchParams.filter[source] = {
        eq: typeof filterValue === 'boolean' ? filterValue : filterValue === 'YES',
      };
    });

    const isPending = searchParams.filter?.isPending?.eq === 'YES';
    if (searchParams.filter) {
      delete searchParams.filter.isPending;
    }

    if (hasFilterByActiveStatus && hasFilterByFutureStatus) {
      const filterKey = `${END_DATE_FILTER_KEY}.${FilterCondition.GE}`;
      const newFilterEndDate = getMaxDate([today, getMomentDateObj(get(request, filterKey) || today)]);

      set(searchParams, filterKey, formatDateForAPI(newFilterEndDate));
    } else if (hasFilterByActiveStatus) {
      const startDateFilterKey = `${START_DATE_FILTER_KEY}.${FilterCondition.LE}`;
      const endDatFilterKey = `${END_DATE_FILTER_KEY}.${FilterCondition.GE}`;

      const newFilterEndDate = getMaxDate([today, getMomentDateObj(get(request, endDatFilterKey) || today)]);
      const newFilterStartDate = getMinDate([today, getMomentDateObj(get(request, startDateFilterKey) || today)]);

      set(searchParams, startDateFilterKey, formatDateForAPI(newFilterStartDate));
      set(searchParams, endDatFilterKey, formatDateForAPI(newFilterEndDate));
    } else if (hasFilterByFutureStatus) {
      const filterKey = `${START_DATE_FILTER_KEY}.${FilterCondition.GE}`;
      const newFilterEndDate = getMaxDate([tomorrow, getMomentDateObj(get(request, filterKey) || tomorrow)]);

      set(searchParams, filterKey, formatDateForAPI(newFilterEndDate));
    }

    if ((hasFilterByActiveStatus || hasFilterByFutureStatus) && !hasExplicitIsVirtualFilter) {
      set(searchParams, IS_VIRTUAL_FILTER_KEY, false);
    }

    delete searchParams.filter.status;

    if (isPending) {
      set(searchParams, STATUS_FILTER_KEY, [PENDING_STATUS]);
    }

    return searchParams;
  };

  const search = async (request: ResourceData, options?: ApiRequestOptions) => {
    const response = await baseSearch(normalizeSearchParams(request), options);

    return {
      ...response,
      result: (response.result || []).map(mapAssignmentWithSpof),
    };
  };

  const searchAll = async (request: ResourceData) => {
    const response = await baseSearchAll(normalizeSearchParams(request));

    return response.map(mapAssignmentWithSpof);
  };

  const getAssignmentsValidationSchema = () => Promise.resolve(assignmentsValidation);
  const getCreateTeamSlotValidationSchema = () => Promise.resolve(createTeamSlotValidation);

  return {
    ...baseCrudRequests,
    getAssignmentsValidationSchema,
    getConfirmAssignmentValidationSchema,
    getCreateTeamSlotValidationSchema,
    getAssignmentById,
    getById,
    getValidationSchema,
    createAssignment,
    update,
    create,
    deleteById,
    confirmVirtual,
    search,
    searchAll,
  };
};

export default useAssignmentService;
